import type { CargoUnidade, PapelKey, UsuarioPublico } from "@caint/shared";
import { gerarSenhaProvisoria } from "@caint/shared";
import { useCallback, useMemo, useState } from "react";
import type { CredencialProntaProps } from "../components/CredencialPronta.tsx";
import type { UsuariosApi } from "../state/useAppState.ts";
import { nomeUnidade } from "./ciclos.ts";
import { mensagemAmigavel } from "./erros.ts";
import {
  type CampoUsuario,
  type FaltaUsuario,
  canonizarEmail,
  faltasUsuario,
  patchUsuario,
} from "./usuarioForm.ts";

export interface UsuarioFormApi {
  ehEdicao: boolean;
  ehUnidade: boolean;
  nome: string;
  setNome: (v: string) => void;
  matricula: string;
  setMatricula: (v: string) => void;
  senha: string;
  setSenha: (v: string) => void;
  papel: PapelKey;
  setPapel: (v: PapelKey) => void;
  /** "" ⇒ sem eixo. */
  eixo: string;
  setEixo: (v: string) => void;
  unidadeId: string;
  setUnidadeId: (v: string) => void;
  cargoUnidade: CargoUnidade;
  setCargoUnidade: (v: CargoUnidade) => void;
  ativo: boolean;
  setAtivo: (v: boolean) => void;
  faltas: FaltaUsuario[];
  /** Já clicou em salvar com algo faltando — a partir daí os erros aparecem. */
  tentou: boolean;
  /** Quantas vezes isso aconteceu: cada nova tentativa leva o foco ao 1º erro. */
  tentativas: number;
  erroCampo: (c: CampoUsuario) => string | undefined;
  erro: string | null;
  salvando: boolean;
  /** Preenchido após CRIAR: a casca troca o formulário pela credencial, sem fechar. */
  criado: CredencialProntaProps | null;
  /** Edição em que o e-mail de login foi alterado (a tela avisa a consequência). */
  emailMudou: boolean;
  /** Há algo digitado/alterado que se perderia ao fechar. */
  sujo: boolean;
  gerarSenha: () => void;
  salvar: () => Promise<void>;
}

export interface UsuarioFormArgs {
  api: UsuariosApi;
  editando?: UsuarioPublico;
  /** Criou ou salvou a edição. Na criação a casca NÃO deve fechar (ver `criado`). */
  onSalvo: (nome: string, tipo: "criado" | "atualizado") => void;
  /** Edição sem mudança nenhuma: fecha sem ir ao servidor. */
  onFechar: () => void;
}

/**
 * Lógica do formulário criar/editar usuário, compartilhada entre mobile e
 * desktop — antes vivia duplicada nas duas telas, e a correção de uma não
 * chegava à outra.
 */
export function useUsuarioForm({ api, editando, onSalvo, onFechar }: UsuarioFormArgs): UsuarioFormApi {
  const [nome, setNome] = useState(editando?.nome ?? "");
  const [matricula, setMatricula] = useState(editando?.matricula ?? "");
  // Senha já nasce forte e legível — evita o gestor repetir a mesma para todos.
  const [senha, setSenha] = useState(() => gerarSenhaProvisoria());
  const [papel, setPapel] = useState<PapelKey>((editando?.papel as PapelKey) ?? "gqa");
  const [eixo, setEixo] = useState(editando?.eixo ?? "");
  const [unidadeId, setUnidadeId] = useState(editando?.unidadeId ?? "");
  const [cargoUnidade, setCargoUnidade] = useState<CargoUnidade>(
    (editando?.cargoUnidade as CargoUnidade) ?? "diretor",
  );
  const [ativo, setAtivo] = useState(editando?.ativo ?? true);
  const [criado, setCriado] = useState<CredencialProntaProps | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [tentativas, setTentativas] = useState(0);
  const tentou = tentativas > 0;

  const ehEdicao = !!editando;
  const ehUnidade = papel === "unidade";
  const faltas = faltasUsuario(
    { nome, matricula, senha, ehUnidade, unidadeId },
    ehEdicao,
    editando?.matricula,
  );
  const erroCampo = (c: CampoUsuario) => (tentou ? faltas.find((f) => f.campo === c)?.msg : undefined);

  const patch = useMemo(
    () =>
      editando
        ? patchUsuario(editando, { nome, matricula, papel, eixo, unidadeId, cargoUnidade, ativo })
        : null,
    [editando, nome, matricula, papel, eixo, unidadeId, cargoUnidade, ativo],
  );
  const emailMudou = patch?.matricula !== undefined;
  const sujo = patch ? Object.keys(patch).length > 0 : nome.trim() !== "" || matricula.trim() !== "";

  const gerarSenha = useCallback(() => setSenha(gerarSenhaProvisoria()), []);

  const criar = async () => {
    const email = canonizarEmail(matricula);
    // setores não é mais usado (conta de unidade vê a unidade inteira).
    await api.criar({
      nome: nome.trim(),
      matricula: email,
      senha,
      papel,
      eixo: ehUnidade ? null : ((eixo || null) as "IMGO" | "IMQA" | "IMGR" | null),
      unidadeId: ehUnidade ? unidadeId || null : null,
      cargoUnidade: ehUnidade ? cargoUnidade : null,
      setores: [],
    });
    // Não fecha: a senha só existe em claro aqui. Fechar antes de copiar
    // obriga a redefinir — o servidor guarda só o hash.
    setCriado({
      nome: nome.trim(),
      matricula: email,
      senha,
      papel,
      unidadeNome: ehUnidade ? nomeUnidade(unidadeId) : undefined,
    });
    setSalvando(false);
    onSalvo(nome.trim(), "criado");
  };

  const salvar = async () => {
    // Botão sempre habilitado: ao clicar, se falta algo, mostra o que é (não some).
    if (faltas.length > 0) {
      setTentativas((n) => n + 1);
      return;
    }
    if (editando && patch && Object.keys(patch).length === 0) {
      onFechar();
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      if (editando && patch) {
        const salvo = await api.atualizar(editando.id, patch);
        // Servidor de uma versão anterior ignora o campo e responde 200 com o
        // e-mail antigo. Sem conferir, a tela diria "atualizado" logo depois de
        // avisar que o login ia mudar — e ninguém saberia que não mudou.
        if (patch.matricula !== undefined && salvo?.matricula && salvo.matricula !== patch.matricula) {
          setErro(
            "O servidor não registrou a troca do e-mail (o restante foi salvo). Ele pode estar sendo atualizado — tente de novo em alguns minutos.",
          );
          setSalvando(false);
          return;
        }
        onSalvo(nome.trim(), "atualizado");
      } else {
        await criar();
      }
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setSalvando(false);
    }
  };

  return {
    ehEdicao,
    ehUnidade,
    nome,
    setNome,
    matricula,
    setMatricula,
    senha,
    setSenha,
    papel,
    setPapel,
    eixo,
    setEixo,
    unidadeId,
    setUnidadeId,
    cargoUnidade,
    setCargoUnidade,
    ativo,
    setAtivo,
    faltas,
    tentou,
    tentativas,
    erroCampo,
    erro,
    salvando,
    criado,
    emailMudou,
    sujo,
    gerarSenha,
    salvar,
  };
}
